import React, { useState } from 'react';
import type { CompanyOption, LanguageOption } from '@ditioas/web-ui';
import {
  Button,
  NavigationMenu,
  NavigationMenuSection,
  Skeleton,
  Tag,
  TopBar,
  TopBarV2,
} from '@ditioas/web-ui';
import { atom, useAtom, useSetAtom } from 'jotai';
import mixpanel from 'mixpanel-browser';
import Helmet from 'react-helmet';
import { useTranslation } from 'react-i18next';
import i18n from 'src/i18n';
import { useMedia } from 'use-media';

import config from 'config/site.config';
import { useGetLegacyDashboard, useGetMenuContent } from 'data/menu';
import {
  useGetMenuPreferences,
  useSaveMenuPreferences,
} from 'data/menu-preferences-api-hooks';
import useCurrentUserAndSettings, {
  currentUserAndSettingsAtom,
  setUserLanguageAtom,
} from 'hooks/useCurrentUserAndSettings';
import useHandleError from 'hooks/useHandleError';
import useObjectSearchParams from 'hooks/useObjectSearchParams';
import useShowAlert from 'hooks/useShowAlert';
import { getUserManager } from 'libs/auth';
import cn from 'libs/cn';
import { getFileUrl } from 'libs/urlFunctions';
import type { LocaleId } from 'model/locale';
import { findLocaleById, getLocaleOptions } from 'model/locale';
import { useLayoutTitleContext } from 'providers/LayoutTitleProvider';
import { setCurrentUserLanguage } from 'services/language-service';
import { changeCompanyProfile } from 'services/user-service';

import LayoutContext from './LayoutContext/LayoutContext';
import PageLink from './PageLink/PageLink';
import ReleaseNotesDialog from './ReleaseNotesDialog/ReleaseNotesDialog';
import SidebarNavV2 from './SidebarNavV2/SidebarNavV2';

const styles = getComputedStyle(document.documentElement);
export const sidebarBreakpoint = styles.getPropertyValue(
  '--breakpoint-sidebarBreakpoint'
);

export interface LayoutURLParams {
  presentationMode: 'enabled';
}

export const navMenuStateAtom = atom(true);

interface Props {
  children: React.ReactNode;
}

const Layout = ({ children }: Props) => {
  const { presentationMode } = useObjectSearchParams<LayoutURLParams>();
  const isInPresentationMode = presentationMode === 'enabled';

  const titleContext = useLayoutTitleContext();
  const heading = titleContext.title;

  const { t } = useTranslation();
  const { currentUser, oidcUser } = useCurrentUserAndSettings();
  const setUserLanguage = useSetAtom(setUserLanguageAtom);
  const setUserAtom = useSetAtom(currentUserAndSettingsAtom);
  const handleError = useHandleError();
  const { showSuccessMessage } = useShowAlert();

  const { menuItems, mutate } = useGetMenuContent();
  const { data: menuPreferences, error: menuPreferencesError } =
    useGetMenuPreferences();
  const saveMenuPreferences = useSaveMenuPreferences();
  // A live error forces the old menu even when cached preferences say v2 —
  // after a backend rollback the cache would otherwise strand the user in a
  // v2 shell whose content and save endpoints all fail.
  const useNewMenu =
    menuPreferences?.useNewMenu === true && !menuPreferencesError;

  const handleTryNewMenu = (): void => {
    // Only opt in once preferences have actually loaded — persisting
    // `favoriteModules: []` here (prefs still fetching, or the endpoint is
    // down and silently errors) would wipe the user's saved favorites.
    if (!menuPreferences) {
      return;
    }

    saveMenuPreferences({ ...menuPreferences, useNewMenu: true });
  };
  const { data, isFetching } = useGetLegacyDashboard(
    !currentUser || (!currentUser.sysOp && !currentUser.isImpersonating)
  );
  const devInfo = data?.data;

  const mounted = React.useRef(false);
  React.useEffect(() => {
    if (mounted.current) {
      mutate();
    } else {
      mounted.current = true;
    }
  }, [i18n.language]);

  const [menuOpen, setMenuOpen] = useAtom(navMenuStateAtom);
  const [forcedCollapsible, setForcedCollapsible] = useState(false);

  const isWide = useMedia(
    {
      minWidth: parseInt(sidebarBreakpoint, 10),
    },
    true
  );
  React.useEffect(() => {
    if (!isWide && menuOpen) {
      setMenuOpen(false);
    }
  }, [isWide]);

  const [language, setLanguage] = useState(
    findLocaleById(currentUser?.languageId)
  );

  const currentCompanyOption = {
    active: true,
    companyId: currentUser ? currentUser.companyId : '',
    companyName: currentUser ? currentUser.companyName : '',
    connectedCompanyName: currentUser ? currentUser.connectedCompanyName : '',
    connectedProfileId: currentUser ? currentUser.userCompanyProfileId : '',
  };

  const companies: CompanyOption[] = currentUser?.userCompanyRoles?.length
    ? currentUser.userCompanyRoles.map((company) => ({
        active: !company.companyIsDisabled,
        companyId: company.companyId,
        companyName: company.companyName,
        connectedCompanyName: company.connectedCompanyName,
        connectedProfileId: company.companyProfileId,
      }))
    : [currentCompanyOption];

  const handleOnLanguageChange = async (
    newLanguage: LanguageOption
  ): Promise<void> => {
    try {
      const { userId } = currentUser;
      await setCurrentUserLanguage(userId, newLanguage.id as LocaleId);
      setUserLanguage(newLanguage.id);
      setLanguage(findLocaleById(newLanguage.id as LocaleId));
      showSuccessMessage({
        message: t('General.LanguageSettingsUpdated_SuccessMessage'),
      });
    } catch (error) {
      handleError(error);
    }
  };

  const handleOnCompanyChange = async (
    company: CompanyOption
  ): Promise<void> => {
    try {
      const response = await changeCompanyProfile(company.connectedProfileId);
      if (response) {
        showSuccessMessage({
          message: t('General.CompanyChanged'),
        });
        const userManager = getUserManager();
        setUserAtom({});
        if (config.mixpanelToken) {
          mixpanel.reset();
        }
        userManager.removeUser();
      }
    } catch (error) {
      handleError(error);
    }
  };

  const contextValue = React.useMemo(
    () => ({ forcedCollapsible, setForcedCollapsible }),
    [forcedCollapsible, setForcedCollapsible]
  );

  React.useEffect(() => {
    if (forcedCollapsible) {
      setMenuOpen(false);
    }
  }, [forcedCollapsible]);

  const topBarCommonProps = {
    title: heading,
    userAvatarSrc: currentUser?.avatarImageId
      ? getFileUrl(currentUser.avatarImageId, 64)
      : null,
    userName: currentUser?.name,
    company: {
      active: true,
      companyId: currentUser ? currentUser.companyId : '',
      companyName: currentUser ? currentUser.companyName : '',
      connectedCompanyName: currentUser ? currentUser.connectedCompanyName : '',
      connectedProfileId: currentUser ? currentUser.userCompanyProfileId : '',
    },
    onCompanyChange: handleOnCompanyChange,
    companyOptions: companies,
    includeInactiveLabel: t('General.IncludeInactive'),
    language,
    onLanguageChange: handleOnLanguageChange,
    languageOptions: getLocaleOptions(language.id as LocaleId),
    logoutButtonText: currentUser.isImpersonating
      ? t('Navigation.ToSysop')
      : t('Navigation.Logout'),
    onLogout: () => {
      if (currentUser.isImpersonating) {
        window.location.href = `${config.legacyWebBaseUrl}#/ngQrCodeRegister`;
      } else {
        const userManager = getUserManager();
        setUserAtom({});
        if (config.mixpanelToken) {
          mixpanel.reset();
        }
        userManager.signoutRedirect({
          id_token_hint: oidcUser.id_token,
        });
        userManager.removeUser();
      }
    },
  };

  // Shared between the classic top bar and TopBarV2 — only the wrappers and a
  // few layout classes differ between the two, so the content lives here once.
  const renderEnvironmentTag = (className?: string): React.ReactNode =>
    (currentUser?.sysOp || currentUser?.isImpersonating) && (
      <Tag
        color={
          import.meta.env.VITE_NODE_ENV === 'production' ? 'orange' : 'azure'
        }
        coloredText
        text={
          import.meta.env.VITE_NODE_ENV === 'production' ? 'Production' : 'Test'
        }
        className={className}
      />
    );

  const renderImpersonatingTag = (textClassName: string): React.ReactNode =>
    currentUser?.isImpersonating && (
      <Tag
        color="green"
        coloredText
        text={`${t('SysOp.Impersonating')}: ${currentUser.companyId} - ${
          currentUser.companyName
        }`}
        textClassName={textClassName}
      />
    );

  const devUsersInfo =
    devInfo?.systemInfo &&
    (isFetching ? (
      <Skeleton className="h-4 w-32.5" />
    ) : (
      <Button
        asChild
        variant="link"
        size="sm"
        data-testid="layout.dev-users-link"
      >
        <a href={`${config.legacyWebBaseUrl}/#/AdminUserOnline`}>
          {`${t('Title.Users')}: ${
            devInfo.systemInfo.totalUserCount
          } ${t('Title.Sessions')}: ${devInfo.systemInfo.sessionsToday}`}
        </a>
      </Button>
    ));

  // Centered items for TopBarV2 (env tags · session counters); the version log
  // renders on the right, before the bar's first divider
  const topBarCenterV2 = (
    <>
      {renderEnvironmentTag('shrink-0')}
      {renderImpersonatingTag('line-clamp-1')}
      {devUsersInfo}
    </>
  );

  const topBarContent = (
    <div className="flex flex-shrink-2 grow items-center justify-center gap-4 px-4">
      <div
        className={cn(
          'mx-auto flex flex-shrink-3 items-center gap-2',
          !currentUser.isImpersonating && 'max-w-52 flex-[1_3_13rem]'
        )}
      >
        {renderEnvironmentTag('w-full flex-shrink-3 justify-center')}
        {renderImpersonatingTag('text-center line-clamp-2')}
      </div>

      <div className="flex items-center gap-4">
        {devUsersInfo}

        <ReleaseNotesDialog />
      </div>
    </div>
  );

  return (
    <LayoutContext.Provider value={contextValue}>
      <div className="relative flex h-screen w-screen overflow-hidden print:h-fit print:overflow-visible">
        {!isInPresentationMode && useNewMenu && <SidebarNavV2 />}
        {!isInPresentationMode && !useNewMenu && (
          <NavigationMenu
            isOpen={menuOpen}
            onClose={() => setMenuOpen(false)}
            logoVariant="brand"
            logoHref={config.legacyWebBaseUrl}
          >
            {menuItems?.map((section) => (
              <NavigationMenuSection key={section.sectionName}>
                {section.pages.map((page) => (
                  <PageLink key={page.name} page={page} />
                ))}
              </NavigationMenuSection>
            )) || (
              <div className="flex flex-col gap-1 px-4">
                <div className="h-7.75 w-47.75 pt-1">
                  <Skeleton className="h-3.75 w-20" />
                </div>
                {Array(8)
                  .fill(null)
                  .map((_, index) => (
                    <Skeleton
                      // eslint-disable-next-line react/no-array-index-key
                      key={`nav-skeleton-${index}`}
                      className="h-10 w-47.75"
                    />
                  ))}
              </div>
            )}

            {/* Hidden (not disabled) until preferences resolve: on a backend
                without the endpoint they never do, and a permanently disabled
                beta button would ship to every user during a staged rollout */}
            {menuPreferences && (
              <div className="mt-auto px-4 pt-4 pb-1">
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="layout.try-new-menu"
                  onClick={handleTryNewMenu}
                  className="w-full justify-center gap-2"
                >
                  {t('Menu.TryNewMenu')}
                  <Tag size="xs" color="azure" text="BETA" />
                </Button>
              </div>
            )}
          </NavigationMenu>
        )}

        <div className="z-5 flex h-screen w-full flex-col overflow-hidden print:h-fit print:overflow-visible">
          {/* No onToggleMenu on TopBarV2: the prop is deprecated and renders
              no button — the working toggle lives in the sidebar header */}
          {!isInPresentationMode && useNewMenu && (
            <TopBarV2 {...topBarCommonProps} centerContent={topBarCenterV2}>
              <ReleaseNotesDialog v2 />
            </TopBarV2>
          )}
          {!isInPresentationMode && !useNewMenu && (
            <TopBar
              {...topBarCommonProps}
              isMenuOpen={menuOpen}
              onToggleMenu={setMenuOpen}
            >
              {topBarContent}
            </TopBar>
          )}

          <Helmet>
            <html lang={language.id} />
            <title>{heading} | Ditio</title>
          </Helmet>
          {children}
        </div>
      </div>
    </LayoutContext.Provider>
  );
};

export default Layout;
